import React, { useState, useEffect } from 'react';
import { Container, Button, Table, Modal, Form, Spinner, Card, Alert, Row, Col } from 'react-bootstrap';
import apiClient, { API_ENDPOINTS } from '../utils/api';
import RecordCard from '../components/RecordCard';
import { formatDateTime, truncateText } from '../utils/helpers';

const initialPropertyForm = {
  property_name: '',
  platform_account_id: '',
  property_id: '',
  address: '',
  city: '',
  country: '日本',
  description: '',
};

const initialAccountForm = {
  platform_id: '',
  account_name: '',
  account_id: '',
  api_key: '',
  api_secret: '',
};

const initialIcalForm = {
  feed_name: '',
  ical_url: '',
};

function Properties() {
  const [properties, setProperties] = useState([]);
  const [platformAccounts, setPlatformAccounts] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [icalFeeds, setIcalFeeds] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [icalLoading, setIcalLoading] = useState(false);
  const [syncingPropertyId, setSyncingPropertyId] = useState(null);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showIcalModal, setShowIcalModal] = useState(false);
  const [propertyForm, setPropertyForm] = useState(initialPropertyForm);
  const [accountForm, setAccountForm] = useState(initialAccountForm);
  const [icalForm, setIcalForm] = useState(initialIcalForm);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const [propertiesRes, accountsRes, platformsRes] = await Promise.all([
        apiClient.get(API_ENDPOINTS.PROPERTIES),
        apiClient.get(API_ENDPOINTS.PLATFORM_ACCOUNTS),
        apiClient.get(API_ENDPOINTS.PLATFORMS),
      ]);

      setProperties(propertiesRes.data);
      setPlatformAccounts(accountsRes.data);
      setPlatforms(platformsRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const getIcalFeedsEndpoint = (propertyId) => `${API_ENDPOINTS.PROPERTIES}/${propertyId}/ical-feeds`;

  const getPlatformName = (platformId) => {
    const platform = platforms.find((item) => item.id === platformId);
    return platform?.name || `ID ${platformId}`;
  };

  const getAccountPropertyCount = (accountId) => (
    properties.filter((property) => property.platform_account_id === accountId).length
  );

  const getIcalFeedCount = (property) => {
    const urls = new Set((property.ical_feeds || []).map((feed) => feed.ical_url));
    if (property.ical_url) urls.add(property.ical_url);
    return urls.size;
  };

  const handlePropertyChange = (e) => {
    const { name, value } = e.target;
    setPropertyForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    setAccountForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleIcalChange = (e) => {
    const { name, value } = e.target;
    setIcalForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateProperty = async () => {
    setSaving(true);
    setError('');
    try {
      await apiClient.post(API_ENDPOINTS.PROPERTIES, {
        ...propertyForm,
        platform_account_id: Number(propertyForm.platform_account_id),
        property_id: propertyForm.property_id || `local-${Date.now()}`,
      });
      setMessage('物件を作成しました');
      setPropertyForm(initialPropertyForm);
      setShowPropertyModal(false);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || '物件の作成に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAccount = async () => {
    setSaving(true);
    setError('');
    try {
      await apiClient.post(API_ENDPOINTS.PLATFORM_ACCOUNTS, {
        ...accountForm,
        platform_id: Number(accountForm.platform_id),
        api_secret: accountForm.api_secret || null,
      });
      setMessage('連携アカウントを作成しました');
      setAccountForm(initialAccountForm);
      setShowAccountModal(false);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || '連携アカウントの作成に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async (account) => {
    const propertyCount = getAccountPropertyCount(account.id);
    const warning = propertyCount > 0
      ? `この連携アカウントに紐づく物件 ${propertyCount}件と関連予約も削除されます。削除しますか？`
      : 'この連携アカウントを削除しますか？';

    if (!window.confirm(warning)) return;

    setSaving(true);
    setError('');
    try {
      await apiClient.delete(`${API_ENDPOINTS.PLATFORM_ACCOUNTS}/${account.id}`);
      setMessage('連携アカウントを削除しました');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || '連携アカウントの削除に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('この物件と関連予約を削除しますか？')) return;

    setError('');
    try {
      await apiClient.delete(`${API_ENDPOINTS.PROPERTIES}/${propertyId}`);
      setMessage('物件を削除しました');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || '物件の削除に失敗しました');
    }
  };

  const fetchIcalFeeds = async (propertyId) => {
    setIcalLoading(true);
    try {
      const response = await apiClient.get(getIcalFeedsEndpoint(propertyId));
      setIcalFeeds(response.data);
      setSelectedProperty((prev) => (
        prev?.id === propertyId ? { ...prev, ical_feeds: response.data } : prev
      ));
    } catch (err) {
      setError(err.response?.data?.detail || 'iCal URLの取得に失敗しました');
    } finally {
      setIcalLoading(false);
    }
  };

  const openIcalModal = async (property) => {
    setSelectedProperty(property);
    setIcalForm(initialIcalForm);
    setShowIcalModal(true);
    await fetchIcalFeeds(property.id);
  };

  const closeIcalModal = () => {
    setShowIcalModal(false);
    setSelectedProperty(null);
    setIcalFeeds([]);
    setIcalForm(initialIcalForm);
  };

  const handleAddIcalFeed = async () => {
    if (!selectedProperty) return;

    setSaving(true);
    setError('');
    try {
      await apiClient.post(getIcalFeedsEndpoint(selectedProperty.id), {
        feed_name: icalForm.feed_name || null,
        ical_url: icalForm.ical_url,
        is_active: true,
      });
      setMessage('iCal URLを追加しました');
      setIcalForm(initialIcalForm);
      await fetchIcalFeeds(selectedProperty.id);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'iCal URLの追加に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIcalFeed = async (feedId) => {
    if (!selectedProperty) return;

    setError('');
    try {
      await apiClient.delete(`${getIcalFeedsEndpoint(selectedProperty.id)}/${feedId}`);
      setMessage('iCal URLを削除しました');
      await fetchIcalFeeds(selectedProperty.id);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'iCal URLの削除に失敗しました');
    }
  };

  const handleSyncIcal = async (property) => {
    setError('');
    setSyncingPropertyId(property.id);
    try {
      const response = await apiClient.post(`${API_ENDPOINTS.PROPERTIES}/${property.id}/sync-ical`);
      const failed = (response.data.feed_results || []).filter((result) => result.status === 'error').length;
      const suffix = failed ? ` / ${failed}件のURLでエラー` : '';
      setMessage(`iCal同期完了: ${response.data.imported}件追加 / ${response.data.skipped}件スキップ${suffix}`);
      await fetchData();
      if (selectedProperty?.id === property.id) {
        await fetchIcalFeeds(property.id);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'iCal同期に失敗しました');
    } finally {
      setSyncingPropertyId(null);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  // 物件行アクション(テーブル/カードで共用)
  const renderPropertyActions = (property, icalCount, syncing) => (
    <>
      <Button variant="outline-secondary" size="sm" onClick={() => openIcalModal(property)}>
        iCal管理
      </Button>
      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => handleSyncIcal(property)}
        disabled={icalCount === 0 || syncing}
      >
        {syncing ? '同期中...' : 'iCal同期'}
      </Button>
      <Button variant="outline-danger" size="sm" onClick={() => handleDeleteProperty(property.id)}>
        削除
      </Button>
    </>
  );

  // iCalフィード行アクション(テーブル/カードで共用)
  const renderIcalFeedActions = (feed) => (
    <Button variant="outline-danger" size="sm" onClick={() => handleDeleteIcalFeed(feed.id)}>
      削除
    </Button>
  );

  return (
    <Container fluid>
      <div className="page-header">
        <h1 className="page-title">物件管理</h1>
        <div>
          <Button variant="outline-primary" onClick={() => setShowAccountModal(true)}>
            連携アカウント追加
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowPropertyModal(true)}
            disabled={platformAccounts.length === 0}
          >
            新規物件
          </Button>
        </div>
      </div>

      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <h4 className="mb-3">連携アカウント</h4>
      <div className="account-grid mb-4">
        {platformAccounts.length === 0 ? (
          <Alert variant="info">物件を登録する前に、連携アカウントを追加してください。</Alert>
        ) : (
          platformAccounts.map((account) => {
            const propertyCount = getAccountPropertyCount(account.id);

            return (
              <Card key={account.id} className="account-card">
                <Card.Body>
                  <div className="account-card-main">
                    <div>
                      <h6>{account.account_name}</h6>
                      <small className="text-muted">
                        {getPlatformName(account.platform_id)} / Account ID: {account.account_id}
                      </small>
                      <div className="mt-2">
                        <span className={`badge ${account.is_active ? 'bg-success' : 'bg-secondary'}`}>
                          {account.is_active ? '有効' : '無効'}
                        </span>
                        <span className="badge bg-light text-dark ms-2">物件 {propertyCount}件</span>
                      </div>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteAccount(account)}
                      disabled={saving}
                    >
                      削除
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            );
          })
        )}
      </div>

      <h4 className="mb-3">物件一覧</h4>
      <div className="d-none d-lg-block">
      <Table striped bordered hover responsive className="data-table">
        <thead>
          <tr>
            <th>物件名</th>
            <th>都市</th>
            <th>国</th>
            <th>iCal URL数</th>
            <th>最終同期</th>
            <th>ステータス</th>
            <th>登録日</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {properties.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center text-muted">物件はまだ登録されていません</td>
            </tr>
          ) : (
            properties.map((property) => {
              const icalCount = getIcalFeedCount(property);
              const syncing = syncingPropertyId === property.id;

              return (
                <tr key={property.id}>
                  <td>{property.property_name}</td>
                  <td>{property.city || '-'}</td>
                  <td>{property.country || '-'}</td>
                  <td>
                    <span className={`badge ${icalCount > 0 ? 'bg-success' : 'bg-secondary'}`}>
                      {icalCount}件
                    </span>
                  </td>
                  <td>{formatDateTime(property.last_ical_sync)}</td>
                  <td>
                    <span className={`badge ${property.is_active ? 'bg-success' : 'bg-secondary'}`}>
                      {property.is_active ? '公開中' : 'オフライン'}
                    </span>
                  </td>
                  <td>{new Date(property.created_at).toLocaleDateString('ja-JP')}</td>
                  <td>
                    <div className="row-actions">{renderPropertyActions(property, icalCount, syncing)}</div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
      </div>

      {/* モバイル: カード型リスト */}
      <div className="d-lg-none">
        {properties.length === 0 ? (
          <p className="text-center text-muted">物件はまだ登録されていません</p>
        ) : (
          properties.map((property) => {
            const icalCount = getIcalFeedCount(property);
            const syncing = syncingPropertyId === property.id;
            return (
              <RecordCard
                key={property.id}
                title={property.property_name}
                subtitle={[property.city, property.country].filter(Boolean).join('・') || ''}
                badges={[
                  <span key="i" className={`badge ${icalCount > 0 ? 'bg-success' : 'bg-secondary'}`}>
                    iCal {icalCount}件
                  </span>,
                  <span key="s" className={`badge ${property.is_active ? 'bg-success' : 'bg-secondary'}`}>
                    {property.is_active ? '公開中' : 'オフライン'}
                  </span>,
                ]}
                fields={[
                  { label: '最終同期', value: formatDateTime(property.last_ical_sync) },
                  { label: '登録日', value: new Date(property.created_at).toLocaleDateString('ja-JP') },
                ]}
                actions={renderPropertyActions(property, icalCount, syncing)}
              />
            );
          })
        )}
      </div>

      <Modal show={showPropertyModal} onHide={() => setShowPropertyModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>新規物件</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>物件名</Form.Label>
                  <Form.Control
                    type="text"
                    name="property_name"
                    value={propertyForm.property_name}
                    onChange={handlePropertyChange}
                    placeholder="物件名を入力"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>連携アカウント</Form.Label>
                  <Form.Select
                    name="platform_account_id"
                    value={propertyForm.platform_account_id}
                    onChange={handlePropertyChange}
                  >
                    <option value="">選択してください</option>
                    {platformAccounts.map((account) => (
                      <option key={account.id} value={account.id}>{account.account_name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>外部物件ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="property_id"
                    value={propertyForm.property_id}
                    onChange={handlePropertyChange}
                    placeholder="未入力の場合は自動採番"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>住所</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={propertyForm.address}
                    onChange={handlePropertyChange}
                    placeholder="住所を入力"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>都市</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={propertyForm.city}
                    onChange={handlePropertyChange}
                    placeholder="都市を入力"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>国</Form.Label>
                  <Form.Control
                    type="text"
                    name="country"
                    value={propertyForm.country}
                    onChange={handlePropertyChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>説明</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={propertyForm.description}
                onChange={handlePropertyChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPropertyModal(false)}>キャンセル</Button>
          <Button
            variant="primary"
            onClick={handleCreateProperty}
            disabled={saving || !propertyForm.property_name || !propertyForm.platform_account_id}
          >
            {saving ? '作成中...' : '作成'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showIcalModal} onHide={closeIcalModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedProperty?.property_name || '物件'} のiCal管理</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProperty?.ical_url && !icalFeeds.some((feed) => feed.ical_url === selectedProperty.ical_url) && (
            <Alert variant="info">
              旧形式のiCal URLが1件残っています。このURLも同期対象です。必要に応じて下の一覧へ追加し直してください。
            </Alert>
          )}

          <Form className="mb-4">
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>表示名</Form.Label>
                  <Form.Control
                    type="text"
                    name="feed_name"
                    value={icalForm.feed_name}
                    onChange={handleIcalChange}
                    placeholder="Airbnb / Agoda など"
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>iCal URL</Form.Label>
                  <Form.Control
                    type="url"
                    name="ical_url"
                    value={icalForm.ical_url}
                    onChange={handleIcalChange}
                    placeholder="https://.../calendar.ics"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Button variant="primary" onClick={handleAddIcalFeed} disabled={saving || !icalForm.ical_url}>
              追加
            </Button>
          </Form>

          {icalLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <>
            <div className="d-none d-lg-block">
            <Table striped bordered hover responsive className="data-table">
              <thead>
                <tr>
                  <th>表示名</th>
                  <th>URL</th>
                  <th>状態</th>
                  <th>最終同期</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {icalFeeds.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">登録済みのiCal URLはありません</td>
                  </tr>
                ) : (
                  icalFeeds.map((feed) => (
                    <tr key={feed.id}>
                      <td>{feed.feed_name}</td>
                      <td title={feed.ical_url}>{truncateText(feed.ical_url, 56)}</td>
                      <td>
                        <span className={`badge ${feed.is_active ? 'bg-success' : 'bg-secondary'}`}>
                          {feed.is_active ? '有効' : '無効'}
                        </span>
                      </td>
                      <td>{formatDateTime(feed.last_sync)}</td>
                      <td>{renderIcalFeedActions(feed)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            </div>

            {/* モバイル: カード型リスト */}
            <div className="d-lg-none">
              {icalFeeds.length === 0 ? (
                <p className="text-center text-muted">登録済みのiCal URLはありません</p>
              ) : (
                icalFeeds.map((feed) => (
                  <RecordCard
                    key={feed.id}
                    title={feed.feed_name}
                    badges={[
                      <span key="s" className={`badge ${feed.is_active ? 'bg-success' : 'bg-secondary'}`}>
                        {feed.is_active ? '有効' : '無効'}
                      </span>,
                    ]}
                    fields={[
                      { label: 'URL', value: truncateText(feed.ical_url, 56) },
                      { label: '最終同期', value: formatDateTime(feed.last_sync) },
                    ]}
                    actions={renderIcalFeedActions(feed)}
                  />
                ))
              )}
            </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={() => selectedProperty && handleSyncIcal(selectedProperty)}
            disabled={!selectedProperty || getIcalFeedCount(selectedProperty) === 0 || syncingPropertyId === selectedProperty?.id}
          >
            {syncingPropertyId === selectedProperty?.id ? '同期中...' : 'この物件を同期'}
          </Button>
          <Button variant="secondary" onClick={closeIcalModal}>閉じる</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAccountModal} onHide={() => setShowAccountModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>連携アカウント</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>プラットフォーム</Form.Label>
              <Form.Select
                name="platform_id"
                value={accountForm.platform_id}
                onChange={handleAccountChange}
              >
                <option value="">選択してください</option>
                {platforms.map((platform) => (
                  <option key={platform.id} value={platform.id}>{platform.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>アカウント名</Form.Label>
              <Form.Control
                type="text"
                name="account_name"
                value={accountForm.account_name}
                onChange={handleAccountChange}
                placeholder="Airbnb Account"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>アカウントID</Form.Label>
              <Form.Control
                type="text"
                name="account_id"
                value={accountForm.account_id}
                onChange={handleAccountChange}
                placeholder="外部サービス側のID"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>APIキー</Form.Label>
              <Form.Control
                type="password"
                name="api_key"
                value={accountForm.api_key}
                onChange={handleAccountChange}
                placeholder="ローカルテストでは任意の値で可"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>APIシークレット</Form.Label>
              <Form.Control
                type="password"
                name="api_secret"
                value={accountForm.api_secret}
                onChange={handleAccountChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAccountModal(false)}>キャンセル</Button>
          <Button
            variant="primary"
            onClick={handleCreateAccount}
            disabled={saving || !accountForm.platform_id || !accountForm.account_name || !accountForm.account_id || !accountForm.api_key}
          >
            {saving ? '作成中...' : '作成'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Properties;
